import React, { ReactElement, ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import { ToolbarCollectionEmptyStateFallback } from '../EmptyState/ToolbarCollectionEmptyStateFallback';
import { TopNotificationBasicRenderProp } from '../TableTopNotification';
import { TaskState } from '@wix/bex-core';
import { ErrorState } from '../ErrorState';

export interface PlaceholderStatesBaseProps {
  state: {
    initTask: TaskState<unknown>;
    showLoadingState: boolean;
    showEmptyState: boolean;
    showErrorState: boolean;
    errorStatus:
      | {
          error: unknown;
        }
      | null
      | undefined;
    retryErrorState: () => void;
  };
  renderError?: (params: {
    err: unknown;
    isOnline: boolean;
    retry: () => void;
  }) => ReactElement;
  renderEmptyState?: () => ReactElement;
  topNotification?: TopNotificationBasicRenderProp;
  loadingState?: ReactNode;
  children?: ReactNode;
}

function _PlaceholderStatesBase(props: PlaceholderStatesBaseProps) {
  const {
    state,
    renderEmptyState,
    renderError,
    topNotification: topNotificationProp,
    children,
    loadingState,
  } = props;

  const {
    initTask,
    showLoadingState,
    showEmptyState,
    showErrorState,
    errorStatus,
  } = state;

  const topNotification =
    typeof topNotificationProp === 'function'
      ? topNotificationProp()
      : topNotificationProp;

  let child: ReactNode;

  if (initTask.status.isError) {
    child = (
      <ErrorState
        dataHook="placeholder-states-error-state"
        errorStatus={initTask.status}
        state={state}
        renderError={renderError}
      />
    );
  } else if (showLoadingState) {
    child = loadingState || children;
  } else if (showErrorState && errorStatus) {
    child = (
      <ErrorState
        dataHook="placeholder-states-error-state"
        errorStatus={errorStatus}
        state={state}
        renderError={renderError}
      />
    );
  } else if (showEmptyState) {
    if (renderEmptyState) {
      child = renderEmptyState();
    } else {
      child = (
        <ToolbarCollectionEmptyStateFallback dataHook="placeholder-states-empty-state" />
      );
    }
  } else {
    child = children;
  }

  return (
    <>
      {topNotification}
      {child}
    </>
  );
}

export const PlaceholderStatesBase = observer(_PlaceholderStatesBase);
